/**
 * Global HR — Sprint 3 · Assets, HR Vendors, Travel & Expenses.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Pencil, X, Laptop, Truck, Phone, KeyRound, Building2, Package, Check, Banknote, MapPin, Download, Users, Plane } from 'lucide-react';
import { apiClient, API } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const Card = ({ children, className = '' }) => (<div className={`bg-white border border-black/5 rounded-2xl ${className}`}>{children}</div>);
const H1 = ({ eyebrow, title, sub }) => (
  <div className="mb-6">
    <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">{eyebrow}</span>
    <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{title}</h1>
    {sub && <p className="mt-1 text-sm text-[#0b1a2e]/60">{sub}</p>}
  </div>
);
const Btn = ({ variant = 'primary', className = '', ...rest }) => {
  const styles = { primary: 'bg-[#0f5c3d] text-white hover:bg-[#0b4a30]', ghost: 'border border-[#0f5c3d]/20 text-[#0f5c3d] hover:bg-[#f7fbf8]', danger: 'text-red-600' }[variant];
  return <button {...rest} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${styles} ${className}`} />;
};
const Input = (p) => <input {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm ${p.className || ''}`} />;
const Select = (p) => <select {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white ${p.className || ''}`}>{p.children}</select>;
const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">{label}</label>{children}
  </div>
);
const Badge = ({ color, children }) => <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${color}`}>{children}</span>;

// ============================================================
// ASSETS
// ============================================================
const CATEGORY_ICONS = { laptop: Laptop, phone: Phone, vehicle: Truck, access_card: KeyRound, furniture: Building2, other: Package };

export const AssetsPage = () => {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, u] = await Promise.all([apiClient.get('/hr/assets'), apiClient.get('/users/directory')]);
      setItems(a.data.data || []); setUsers(u.data.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const returnIt = async (a) => {
    const c = window.prompt('Condition on return (optional):', '');
    try { await apiClient.post(`/hr/assets/${a.id}/return`, { condition_on_return: c || null }); toast({ title: 'Asset returned' }); load(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const del = async (a) => { if (window.confirm(`Delete ${a.name}?`)) { await apiClient.delete(`/hr/assets/${a.id}`); load(); } };
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · Sprint 3" title="Assets register" sub="Laptops, phones, vehicles, access cards. Track custody and print acceptance letters." />
        <div className="flex gap-2 mb-6"><Btn onClick={() => setEditing({})} data-testid="new-asset-btn"><Plus className="w-3.5 h-3.5" /> New asset</Btn></div>
      </div>
      <Card>
        {loading ? <div className="p-10 text-center"><Loader2 className="w-5 h-5 inline animate-spin" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr>
                <th className="text-left p-2">Code</th><th className="text-left p-2">Name</th><th className="text-left p-2">Category</th><th className="text-left p-2">Serial</th><th className="text-left p-2">Status</th><th className="text-left p-2">Assigned to</th><th></th>
              </tr></thead>
              <tbody>{items.map((a) => {
                const Icon = CATEGORY_ICONS[a.category] || Package;
                const active = (a.assignments || []).slice(-1)[0];
                const assigned = active && !active.returned_date ? active : null;
                return (
                  <tr key={a.id} className="border-t border-black/5" data-testid={`asset-row-${a.asset_code}`}>
                    <td className="p-2 font-mono text-xs">{a.asset_code}</td>
                    <td className="p-2"><Icon className="w-3.5 h-3.5 inline text-[#0f5c3d] mr-1.5" />{a.name}</td>
                    <td className="p-2 text-xs">{a.category}</td>
                    <td className="p-2 text-xs">{a.serial_number || '—'}</td>
                    <td className="p-2"><Badge color={a.status === 'assigned' ? 'bg-amber-100 text-amber-800' : a.status === 'in_stock' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>{a.status}</Badge></td>
                    <td className="p-2 text-xs">{assigned ? assigned.user_name : '—'}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {a.status === 'in_stock' && <Btn variant="ghost" onClick={() => setAssigning(a)} data-testid={`assign-${a.asset_code}`}>Assign</Btn>}
                      {a.status === 'assigned' && (<>
                        <a href={`${API}/api/hr/assets/${a.id}/acceptance-letter.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0f5c3d] text-xs mr-2"><Download className="w-3.5 h-3.5" /> Letter</a>
                        <Btn variant="ghost" onClick={() => returnIt(a)} data-testid={`return-${a.asset_code}`}>Return</Btn>
                      </>)}
                      <button onClick={() => setEditing(a)} className="text-[#0f5c3d] ml-2"><Pencil className="w-3.5 h-3.5 inline" /></button>
                      <button onClick={() => del(a)} className="text-red-600 ml-1"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </Card>
      {editing && <AssetModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {assigning && <AssignAssetModal asset={assigning} users={users} onClose={() => setAssigning(null)} onSaved={() => { setAssigning(null); load(); }} />}
    </div>
  );
};

const AssetModal = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState({ asset_code: '', name: '', category: 'laptop', serial_number: '', make_model: '', purchase_date: '', purchase_cost: '', country: '', status: 'in_stock', notes: '', ...initial });
  const save = async () => {
    try {
      const payload = { ...f, purchase_cost: f.purchase_cost ? parseFloat(f.purchase_cost) : null, purchase_date: f.purchase_date || null };
      if (f.id) await apiClient.patch(`/hr/assets/${f.id}`, payload); else await apiClient.post('/hr/assets', payload);
      toast({ title: 'Saved' }); onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">{f.id ? 'Edit' : 'New'} asset</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Asset code"><Input value={f.asset_code} onChange={(e) => setF({ ...f, asset_code: e.target.value })} data-testid="a-code" /></Field>
          <Field label="Category"><Select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="a-cat">{['laptop', 'phone', 'vehicle', 'access_card', 'furniture', 'other'].map((c) => <option key={c} value={c}>{c}</option>)}</Select></Field>
          <div className="col-span-2"><Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="a-name" /></Field></div>
          <Field label="Serial"><Input value={f.serial_number} onChange={(e) => setF({ ...f, serial_number: e.target.value })} /></Field>
          <Field label="Make / Model"><Input value={f.make_model} onChange={(e) => setF({ ...f, make_model: e.target.value })} /></Field>
          <Field label="Purchase date"><Input type="date" value={f.purchase_date || ''} onChange={(e) => setF({ ...f, purchase_date: e.target.value })} /></Field>
          <Field label="Purchase cost"><Input type="number" value={f.purchase_cost || ''} onChange={(e) => setF({ ...f, purchase_cost: e.target.value })} /></Field>
          <Field label="Country"><Input value={f.country || ''} onChange={(e) => setF({ ...f, country: e.target.value.toUpperCase() })} placeholder="IN" /></Field>
          <Field label="Status"><Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>{['in_stock', 'assigned', 'retired', 'lost'].map((s) => <option key={s} value={s}>{s}</option>)}</Select></Field>
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="a-save">Save</Btn></div>
        </div>
      </div>
    </div>
  );
};

const AssignAssetModal = ({ asset, users, onClose, onSaved }) => {
  const [f, setF] = useState({ user_id: '', assigned_date: new Date().toISOString().slice(0, 10), condition_on_assign: 'Good' });
  const save = async () => {
    if (!f.user_id) return toast({ title: 'Pick a user', variant: 'destructive' });
    try { await apiClient.post(`/hr/assets/${asset.id}/assign`, f); toast({ title: 'Assigned' }); onSaved(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3">Assign {asset.name}</div>
        <div className="space-y-3">
          <Field label="Employee"><Select value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="assign-user"><option value="">Select</option>{users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}</Select></Field>
          <Field label="Date"><Input type="date" value={f.assigned_date} onChange={(e) => setF({ ...f, assigned_date: e.target.value })} /></Field>
          <Field label="Condition"><Input value={f.condition_on_assign} onChange={(e) => setF({ ...f, condition_on_assign: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="assign-submit">Assign</Btn></div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// HR VENDORS
// ============================================================
export const HRVendorsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await apiClient.get('/hr/hr-vendors'); setItems(r.data.data || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (v) => { if (window.confirm(`Delete ${v.name}?`)) { await apiClient.delete(`/hr/hr-vendors/${v.id}`); load(); } };
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · Sprint 3" title="HR vendors" sub="Recruiters, background-check firms, insurance brokers, training providers with contract-expiry alerts." />
        <div className="flex gap-2 mb-6"><Btn onClick={() => setEditing({})} data-testid="new-vendor-btn"><Plus className="w-3.5 h-3.5" /> New vendor</Btn></div>
      </div>
      <Card>
        {loading ? <div className="p-10 text-center"><Loader2 className="w-5 h-5 inline animate-spin" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Name</th><th className="text-left p-2">Kind</th><th className="text-left p-2">Contact</th><th className="text-left p-2">Country</th><th className="text-left p-2">Contract end</th><th></th></tr></thead>
            <tbody>{items.map((v) => (
              <tr key={v.id} className={`border-t border-black/5 ${v.is_expiring ? 'bg-amber-50' : ''}`} data-testid={`vendor-row-${v.id}`}>
                <td className="p-2 font-medium">{v.name}</td>
                <td className="p-2 text-xs">{v.kind}</td>
                <td className="p-2 text-xs">{v.contact_person} · {v.email || v.phone || '-'}</td>
                <td className="p-2 text-xs">{v.country || '—'}</td>
                <td className="p-2 text-xs">{v.contract_end || '—'} {v.is_expiring && <Badge color="bg-amber-100 text-amber-800">expiring</Badge>}</td>
                <td className="p-2 text-right"><button onClick={() => setEditing(v)} className="text-[#0f5c3d] mr-2"><Pencil className="w-3.5 h-3.5 inline" /></button><button onClick={() => del(v)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 inline" /></button></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </Card>
      {editing && <VendorModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const VendorModal = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState({ name: '', kind: 'recruiter', contact_person: '', email: '', phone: '', country: '', contract_start: '', contract_end: '', fees: '', notes: '', active: true, ...initial });
  const save = async () => {
    try { const p = { ...f, contract_start: f.contract_start || null, contract_end: f.contract_end || null };
      if (f.id) await apiClient.patch(`/hr/hr-vendors/${f.id}`, p); else await apiClient.post('/hr/hr-vendors', p);
      toast({ title: 'Saved' }); onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">{f.id ? 'Edit' : 'New'} vendor</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="v-name" /></Field>
          <Field label="Kind"><Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>{['recruiter', 'background_check', 'insurance', 'training', 'travel', 'health', 'legal', 'other'].map((k) => <option key={k} value={k}>{k}</option>)}</Select></Field>
          <Field label="Contact person"><Input value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} /></Field>
          <Field label="Country"><Input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value.toUpperCase() })} /></Field>
          <Field label="Email"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
          <Field label="Contract start"><Input type="date" value={f.contract_start || ''} onChange={(e) => setF({ ...f, contract_start: e.target.value })} /></Field>
          <Field label="Contract end"><Input type="date" value={f.contract_end || ''} onChange={(e) => setF({ ...f, contract_end: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Fees / rate card"><Input value={f.fees} onChange={(e) => setF({ ...f, fees: e.target.value })} /></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="v-save">Save</Btn></div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// TRAVEL
// ============================================================
export const TravelPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await apiClient.get('/hr/travel'); setItems(r.data.data || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, decision) => {
    try { await apiClient.post(`/hr/travel/${id}/decide`, { decision }); toast({ title: decision }); load(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const settle = async (id) => { try { await apiClient.post(`/hr/travel/${id}/settle`); toast({ title: 'Settled' }); load(); } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); } };
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · Sprint 3" title="Travel & expenses" sub="Trip requests, per-diems, expense reports and settlement." />
        <div className="flex gap-2 mb-6"><Btn onClick={() => setShowNew(true)} data-testid="new-travel-btn"><Plus className="w-3.5 h-3.5" /> New trip</Btn></div>
      </div>
      <Card>
        {loading ? <div className="p-10 text-center"><Loader2 className="w-5 h-5 inline animate-spin" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Purpose</th><th className="text-left p-2">Destination</th><th className="text-left p-2">Dates</th><th className="text-right p-2">Estimate</th><th className="text-right p-2">Actual</th><th className="text-left p-2">Status</th><th></th></tr></thead>
            <tbody>{items.map((t) => (
              <React.Fragment key={t.id}>
                <tr className="border-t border-black/5" data-testid={`travel-row-${t.id}`}>
                  <td className="p-2 font-medium">{t.purpose}</td>
                  <td className="p-2 text-xs"><MapPin className="w-3.5 h-3.5 inline text-[#0f5c3d] mr-1" />{t.destination_city} {t.destination_country && `· ${t.destination_country}`}</td>
                  <td className="p-2 text-xs">{t.depart_date} → {t.return_date}</td>
                  <td className="p-2 text-right text-xs">{t.estimated_cost || '—'} {t.currency || ''}</td>
                  <td className="p-2 text-right font-semibold">{t.total_actual || 0}</td>
                  <td className="p-2"><Badge color={t.status === 'pending' ? 'bg-amber-100 text-amber-800' : t.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : t.status === 'settled' ? 'bg-slate-200 text-slate-800' : t.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-sky-100 text-sky-800'}>{t.status}</Badge></td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <button onClick={() => setExpanded(expanded === t.id ? null : t.id)} className="text-[#0f5c3d] text-xs">{expanded === t.id ? 'Hide' : 'Details'}</button>
                    {t.status === 'pending' && (<>
                      <Btn variant="ghost" onClick={() => decide(t.id, 'approved')} data-testid={`t-approve-${t.id}`} className="ml-2">Approve</Btn>
                      <Btn variant="ghost" onClick={() => decide(t.id, 'rejected')} className="ml-1"><span className="text-red-600">Reject</span></Btn>
                    </>)}
                    {t.status === 'report_submitted' && <Btn variant="ghost" onClick={() => settle(t.id)} data-testid={`t-settle-${t.id}`} className="ml-2"><Banknote className="w-3.5 h-3.5" /> Settle</Btn>}
                  </td>
                </tr>
                {expanded === t.id && <tr className="bg-slate-50"><td colSpan={7} className="p-4"><TravelDetail travel={t} onChanged={load} /></td></tr>}
              </React.Fragment>
            ))}</tbody>
          </table>
        )}
      </Card>
      {showNew && <NewTravelModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
};

const TravelDetail = ({ travel, onChanged }) => {
  const [newExp, setNewExp] = useState({ date: new Date().toISOString().slice(0, 10), category: 'transport', description: '', amount: '', currency: travel.currency || 'USD' });
  const [note, setNote] = useState(travel.report_note || '');
  const addExpense = async () => {
    if (!newExp.amount) return;
    try { await apiClient.post(`/hr/travel/${travel.id}/expenses`, { ...newExp, amount: parseFloat(newExp.amount) }); toast({ title: 'Expense added' }); setNewExp({ ...newExp, description: '', amount: '' }); onChanged(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const submitReport = async () => {
    try { await apiClient.post(`/hr/travel/${travel.id}/submit-report`, { note }); toast({ title: 'Report submitted' }); onChanged(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-2">Expenses</div>
        {(travel.expenses || []).length === 0 ? <div className="text-xs text-[#0b1a2e]/50">No expenses yet.</div> :
          <table className="w-full text-xs"><tbody>{(travel.expenses || []).map((e) => (<tr key={e.id} className="border-t border-black/5"><td className="p-1">{e.date}</td><td className="p-1">{e.category}</td><td className="p-1 text-[#0b1a2e]/60">{e.description}</td><td className="p-1 text-right font-mono">{e.amount} {e.currency}</td></tr>))}</tbody></table>
        }
        <div className="mt-3 grid grid-cols-5 gap-1">
          <Input type="date" value={newExp.date} onChange={(e) => setNewExp({ ...newExp, date: e.target.value })} className="text-xs col-span-1" />
          <Input value={newExp.category} onChange={(e) => setNewExp({ ...newExp, category: e.target.value })} placeholder="cat" className="text-xs col-span-1" />
          <Input value={newExp.description} onChange={(e) => setNewExp({ ...newExp, description: e.target.value })} placeholder="desc" className="text-xs col-span-2" />
          <Input type="number" value={newExp.amount} onChange={(e) => setNewExp({ ...newExp, amount: e.target.value })} placeholder="0" className="text-xs col-span-1" />
        </div>
        <div className="mt-2 flex justify-end"><Btn onClick={addExpense} data-testid={`add-exp-${travel.id}`}>Add expense</Btn></div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-2">Report note</div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="w-full text-xs border border-slate-300 rounded p-2" placeholder="Summary of trip outcomes…" />
        <div className="mt-2 flex justify-end"><Btn onClick={submitReport} data-testid={`t-submit-${travel.id}`}>Submit report</Btn></div>
      </div>
    </div>
  );
};

const NewTravelModal = ({ onClose, onSaved }) => {
  const [f, setF] = useState({ purpose: '', destination_city: '', destination_country: '', depart_date: '', return_date: '', estimated_cost: '', currency: 'USD', advance_needed: 0, per_diem_amount: 0, per_diem_days: 0, notes: '' });
  const save = async () => {
    try { await apiClient.post('/hr/travel', { ...f, estimated_cost: f.estimated_cost ? parseFloat(f.estimated_cost) : null, advance_needed: parseFloat(f.advance_needed || 0), per_diem_amount: parseFloat(f.per_diem_amount || 0), per_diem_days: parseInt(f.per_diem_days || 0, 10) }); toast({ title: 'Submitted' }); onSaved(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-xl rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">New travel request</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label="Purpose"><Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="t-purpose" /></Field></div>
          <Field label="Destination city"><Input value={f.destination_city} onChange={(e) => setF({ ...f, destination_city: e.target.value })} data-testid="t-city" /></Field>
          <Field label="Country"><Input value={f.destination_country} onChange={(e) => setF({ ...f, destination_country: e.target.value.toUpperCase() })} /></Field>
          <Field label="Depart"><Input type="date" value={f.depart_date} onChange={(e) => setF({ ...f, depart_date: e.target.value })} data-testid="t-depart" /></Field>
          <Field label="Return"><Input type="date" value={f.return_date} onChange={(e) => setF({ ...f, return_date: e.target.value })} data-testid="t-return" /></Field>
          <Field label="Estimated cost"><Input type="number" value={f.estimated_cost} onChange={(e) => setF({ ...f, estimated_cost: e.target.value })} /></Field>
          <Field label="Currency"><Input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })} /></Field>
          <Field label="Advance"><Input type="number" value={f.advance_needed} onChange={(e) => setF({ ...f, advance_needed: e.target.value })} /></Field>
          <Field label="Per-diem × days"><Input type="number" value={f.per_diem_days} onChange={(e) => setF({ ...f, per_diem_days: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="t-save">Submit</Btn></div>
        </div>
      </div>
    </div>
  );
};
